import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../config/apiConfig';
import { IoRefreshOutline, IoSend } from 'react-icons/io5';
import {
  getChatRooms,
  createChatRooms,
  getChatMessagesByRoomId,
  sendChatMessage,
  getAllStoresForChat,
  getAdminInfo
} from '../api/chatApi';

const ChatModal = ({ chatUser, onClose, onMinimize }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);
  const intervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const maxRetries = 3;
  const [chatType, setChatType] = useState('store'); // Track chat type

  useEffect(() => {
    if (chatUser && user && token) {
      initializeChat();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [chatUser, user, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const initializeChat = async () => {
    if (!user || !token) {
      setError('Authentication required. Please log in to use chat.');
      setLoading(false);
      return;
    }

    if (!isOnline) {
      setError('No internet connection. Please check your connection and try again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Group chat with both store and admin
      // In Pure Meat, participants can be comma-separated IDs like "2,1" for store+admin
      let participantId = '2,1'; // Store ID 2 and Admin ID 1
      let currentChatType = 'store'; // Use store as primary type
      setChatType(currentChatType);

      console.log('Initializing customer support chat...', { participantId });

      // Get store info for display
      const storeResponse = await getAllStoresForChat();
      console.log('Store response:', storeResponse);
      if (storeResponse.success && storeResponse.data && storeResponse.data.length > 0) {
        const selectedStore = storeResponse.data[0];
        setStoreInfo({
          id: participantId,
          name: 'Customer Support',
          address: 'We\'re here to help',
          phone: '+1 248 497 9083',
          type: 'group'
        });

      } else {
        setStoreInfo({
          id: participantId,
          name: 'Customer Support',
          address: 'We\'re here to help',
          phone: '+1 248 497 9083',
          type: 'group'
        });

      }



      // Try to get existing chat room
      console.log('Trying to get existing chat room...');
      const roomResponse = await getChatRooms(user.id, participantId, token, currentChatType);
      console.log('Room response:', roomResponse);

      if (roomResponse.success) {
        if (roomResponse.data?.id) {

          setRoomId(roomResponse.data.id);
          await loadMessages(roomResponse.data.id);
        } else if (roomResponse.data2?.id) {

          setRoomId(roomResponse.data2.id);
          await loadMessages(roomResponse.data2.id);
        } else {

          // Create new chat room
          await createNewChatRoom(participantId);
        }
      } else {

        // Create new chat room
        await createNewChatRoom(participantId);
      }

      // Start polling for new messages with shorter interval
      startMessagePolling();
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      setError('Failed to connect to chat. Please try again.');
      setLoading(false);
    }
  };

  const loadMessages = async (roomId) => {
    try {
      const messagesResponse = await getChatMessagesByRoomId(roomId, token);
      if (messagesResponse.success) {
        const loadedMessages = messagesResponse.data || [];
        setMessages(loadedMessages);
        setLastMessageCount(loadedMessages.length);
      } else {
        throw new Error(messagesResponse.message || 'Failed to load messages');
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      setError('Failed to load messages. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const createNewChatRoom = async (participantId) => {
    try {
      const createResponse = await createChatRooms(user.id, participantId, token, chatType);
      if (createResponse.success && createResponse.data?.id) {
        setRoomId(createResponse.data.id);
        await loadMessages(createResponse.data.id);
      } else {
        throw new Error(createResponse.message || 'Failed to create chat room');
      }
    } catch (error) {
      console.error('Failed to create chat room:', error);
      setError('Failed to create chat room. Please try again.');
      setLoading(false);
    }
  };

  const retryInitializeChat = () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setError(null);
      initializeChat();
    } else {
      setError('Maximum retry attempts reached. Please refresh the page and try again.');
    }
  };

  // Play notification sound for new messages
  const playNotificationSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Silently fail if audio is not supported

    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || !roomId || sending || !isOnline) {
      return;
    }

    if (!token) {
      setError('Authentication expired. Please log in again.');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const messageData = {
        room_id: roomId,
        uid: user.id,
        from_id: user.id,
        message: newMessage.trim(),
        message_type: 0, // Text message
        status: 1
      };

      const response = await sendChatMessage(messageData, token);

      if (response.success) {
        setNewMessage('');
        // Reload messages immediately to show the sent message
        await loadMessages(roomId);
      } else {
        throw new Error(response.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const refreshChat = () => {
    setRetryCount(0);
    setError(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    initializeChat();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Handle typing indicator
    if (!isTyping) {
      setIsTyping(true);
    }

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout to stop typing indicator
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 1000); // Stop typing after 1 second of inactivity

    setTypingTimeout(timeout);
  };

  const startMessagePolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Poll every 12 seconds to match Pure Meat app
    intervalRef.current = setInterval(async () => {
      if (roomId && isOnline && !error) {
        try {
          const messagesResponse = await getChatMessagesByRoomId(roomId, token);
          if (messagesResponse.success) {
            const newMessages = messagesResponse.data || [];
            const newMessageCount = newMessages.length;

            // Check if there are new messages from other users
            if (newMessageCount > lastMessageCount) {
              const newIncomingMessages = newMessages.slice(lastMessageCount).filter(
                msg => msg.from_id !== user?.id
              );

              if (newIncomingMessages.length > 0) {
                // Play notification sound for new incoming messages
                playNotificationSound();
              }
            }

            // Only update if there are actually new messages
            if (newMessageCount !== messages.length) {
              setMessages(newMessages);
              setLastMessageCount(newMessageCount);
            }
          }
        } catch (error) {
          console.error('Failed to poll messages:', error);
          // Don't show error for polling failures to avoid spam
        }
      }
    }, 12000); // 12 seconds to match Pure Meat app
  };

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="chat-modal-header">
          <div className="chat-user-info">
            <div className="chat-user-avatar">
              <img
                src="/assets/img/logo/nav-log.png"
                alt="Tazza Tropics"
                className="rounded-circle"
                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.src = '/assets/img/product/default.png';
                }}
              />
            </div>
            <div className="chat-user-details">
              <h5 className="chat-user-name">
                {storeInfo ? storeInfo.name : 'Customer Support'}
              </h5>
              <span className={`chat-user-status ${!isOnline ? 'offline' : ''}`}>
                {!isOnline ? 'Offline' : 'Online'}
              </span>
              {storeInfo && storeInfo.address && (
                <small className="text-muted d-block mt-1">
                  {storeInfo.address}
                </small>
              )}
            </div>
          </div>
          <div className="chat-header-actions">
            <button
              className="chat-minimize-btn"
              onClick={onMinimize}
              title="Minimize chat"
            >
              <i className="fas fa-minus"></i>
            </button>
            <button
              className={`chat-refresh-btn ${loading ? 'rotating' : ''}`}
              onClick={refreshChat}
              title="Refresh chat"
              disabled={loading}
            >
              <IoRefreshOutline size={20} />
            </button>
            <button className="chat-close-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="chat-modal-body">
          {error && (
            <div className="chat-error">
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <div className="mt-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={retryCount < maxRetries ? retryInitializeChat : refreshChat}
                  >
                    <i className="fas fa-redo me-1"></i>
                    {retryCount < maxRetries ? 'Retry' : 'Refresh'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="chat-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading messages...</p>
            </div>
          ) : (
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-empty">
                  <div className="text-center">
                    <i className="fas fa-headset fa-2x text-muted mb-3"></i>
                    <p className="mb-2">No messages yet. Start the conversation!</p>
                    <small className="text-muted">
                      Our customer support team typically responds within 5 minutes during business hours.
                    </small>
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`chat-message ${message.from_id === user?.id ? 'sent' : 'received'}`}
                  >
                    <div className="chat-message-content">
                      <p className="chat-message-text">{message.message}</p>
                      <span className="chat-message-time">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {sending && (
            <div className="chat-sending-indicator">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Sending...</span>
              </div>
              <span className="ms-2">Sending message...</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="chat-modal-footer">
          <form onSubmit={handleSendMessage} className="chat-form">
            <div className="chat-input-group">
              <input
                type="text"
                className="chat-input"
                placeholder={error ? "Chat unavailable" : "Type your message..."}
                value={newMessage}
                onChange={handleInputChange}
                disabled={sending || !!error || !isOnline}
              />
              <button
                type="submit"
                className="chat-send-btn"
                disabled={!newMessage.trim() || sending || !!error || !isOnline}
                title={!isOnline ? "No internet connection" : error ? "Chat unavailable" : "Send message"}
              >
                <IoSend size={20} />
              </button>
            </div>
            {!isOnline && (
              <div className="chat-connection-status">
                <small className="text-warning">
                  <i className="fas fa-wifi-slash me-1"></i>
                  You're offline. Messages will be sent when connection is restored.
                </small>
              </div>
            )}
          </form>
        </div>
      </div>

      <style jsx>{`
        .chat-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .chat-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .chat-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid #e9ecef;
          background: #f8f9fa;
          border-radius: 12px 12px 0 0;
        }

        .chat-header-actions {
          display: flex;
          gap: 10px;
        }

        .chat-minimize-btn {
          background: none;
          border: none;
          font-size: 16px;
          color: #6c757d;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .chat-minimize-btn:hover:not(:disabled) {
          background: #e9ecef;
          color: #007bff;
        }

        .chat-minimize-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .chat-refresh-btn {
          background: none;
          border: none;
          font-size: 16px;
          color: #6c757d;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-refresh-btn:hover:not(:disabled) {
          background: #e9ecef;
          color: #007bff;
        }

        .chat-refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .chat-refresh-btn:active:not(:disabled) {
          animation: rotate-refresh 0.6s ease-in-out;
        }

        .chat-refresh-btn.rotating {
          animation: rotate-refresh-continuous 1s linear infinite;
        }

        @keyframes rotate-refresh {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotate-refresh-continuous {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .chat-user-info {
          display: flex;
          align-items: center;
        }

        .chat-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 12px;
        }

        .chat-user-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-icon {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
        }

        .admin-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .store-icon {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .group-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .chat-user-name {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .chat-user-status.offline {
          color: #dc3545;
        }

        .chat-error {
          padding: 10px 20px;
          border-bottom: 1px solid #e9ecef;
        }

        .chat-connection-status {
          margin-top: 8px;
          text-align: center;
        }

        .chat-empty {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        .chat-empty i {
          margin-bottom: 15px;
        }

        .chat-close-btn {
          background: none;
          border: none;
          font-size: 18px;
          color: #6c757d;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .chat-close-btn:hover {
          background: #e9ecef;
          color: #333;
        }

        .chat-modal-body {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          max-height: 400px;
        }

        .chat-loading, .chat-empty {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        .chat-messages {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .chat-message {
          display: flex;
          margin-bottom: 8px;
        }

        .chat-message.sent {
          justify-content: flex-end;
        }

        .chat-message.received {
          justify-content: flex-start;
        }

        .chat-message-content {
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 18px;
          position: relative;
        }

        .chat-message.sent .chat-message-content {
          background: #007bff;
          color: white;
          border-bottom-right-radius: 4px;
        }

        .chat-message.received .chat-message-content {
          background: #f1f3f4;
          color: #333;
          border-bottom-left-radius: 4px;
        }

        .chat-message-text {
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
        }

        .chat-message-time {
          font-size: 11px;
          opacity: 0.7;
          margin-top: 4px;
          display: block;
        }

        .chat-sending-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          color: #6c757d;
          font-size: 14px;
        }

        .chat-modal-footer {
          padding: 20px;
          border-top: 1px solid #e9ecef;
          background: #f8f9fa;
          border-radius: 0 0 12px 12px;
        }

        .chat-form {
          width: 100%;
        }

        .chat-input-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .chat-input {
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #e9ecef;
          border-radius: 25px;
          outline: none;
          font-size: 14px;
          transition: border-color 0.3s ease;
        }

        .chat-input:focus {
          border-color: #007bff;
        }

        .chat-send-btn {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 50%;
          background: #007bff;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .chat-send-btn:hover:not(:disabled) {
          background: #0056b3;
          transform: scale(1.05);
        }

        .chat-send-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        @media (max-width: 576px) {
          .chat-modal {
            width: 95%;
            max-height: 90vh;
          }

          .chat-modal-header,
          .chat-modal-body,
          .chat-modal-footer {
            padding: 15px;
          }

          .chat-message-content {
            max-width: 85%;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatModal;